import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../services/api';
import { isMaxWebApp, isTelegramWebApp } from '../utils/platform';
import { resolveTelegramBotUsername } from '../utils/telegramBotUsername';
import './UnauthorizedScreen.css';

export const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const botUsername = resolveTelegramBotUsername();
  const isInMessengerWebApp = isTelegramWebApp() || isMaxWebApp();

  useEffect(() => {
    if (!isInMessengerWebApp) return;

    // In Telegram/Max WebApp auth is already provided via init data — verify immediately
    // and bounce to the app.
    setIsAuthenticating(true);
    setError(null);
    apiClient
      .verifyAuth()
      .then((response) => {
        if (response.success && response.authenticated) {
          navigate('/', { replace: true });
        } else {
          setError(response.error || 'Authentication failed');
        }
      })
      .catch(() => {
        setError('Authentication failed. Please try again.');
      })
      .finally(() => {
        setIsAuthenticating(false);
      });
  }, [isInMessengerWebApp, navigate]);

  useEffect(() => {
    // Parse Login Widget callback params from URL
    const urlParams = new URLSearchParams(location.search);
    const authData: Record<string, string> = {};

    ['id', 'first_name', 'last_name', 'username', 'photo_url', 'auth_date', 'hash'].forEach((key) => {
      const value = urlParams.get(key);
      if (value) {
        authData[key] = value;
      }
    });

    if (authData.id && authData.hash) {
      setIsAuthenticating(true);
      setError(null);

      // Save to localStorage so API interceptor sends it with requests
      localStorage.setItem('telegram_login_widget_data', JSON.stringify(authData));

      // Verify auth (request will include params from interceptor)
      apiClient
        .verifyAuth()
        .then((response) => {
          if (response.success && response.authenticated) {
            navigate('/', { replace: true });
          } else {
            setError(response.error || 'Authentication failed');
            localStorage.removeItem('telegram_login_widget_data');
          }
        })
        .catch(() => {
          setError('Authentication failed. Please try again.');
          localStorage.removeItem('telegram_login_widget_data');
        })
        .finally(() => {
          setIsAuthenticating(false);
        });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (!botUsername) return;
    if (isInMessengerWebApp) return;

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-auth-url', window.location.origin + '/auth/telegram');
    script.setAttribute('data-request-access', 'write');
    script.async = true;

    const container = document.getElementById('telegram-login-container');
    if (container) {
      container.innerHTML = '';
      container.appendChild(script);
    }

    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [botUsername, isInMessengerWebApp]);

  return (
    <div className="unauthorized-screen">
      <div className="unauthorized-content">
        <div className="unauthorized-icon">🔒</div>
        <h1 className="unauthorized-title">Требуется авторизация</h1>
        <div className="unauthorized-details">
          <p>Авторизуйтесь через Telegram для продолжения.</p>
        </div>

        {isAuthenticating ? (
          <div className="app-loading">
            <p>Авторизация...</p>
          </div>
        ) : isInMessengerWebApp ? (
          <div className="app-loading">
            <p>Авторизация...</p>
          </div>
        ) : (
          <div className="telegram-login-section">
            <p className="telegram-login-text">Авторизуйтесь через Telegram:</p>
            <div id="telegram-login-container" className="telegram-login-widget" />
            {!botUsername && (
              <div className="widget-error">
                <p>Telegram виджет не настроен</p>
                <small>Установите VITE_TELEGRAM_BOT_USERNAME в .env</small>
              </div>
            )}
          </div>
        )}

        {error && (
          <p className="unauthorized-hint" style={{ color: 'var(--error)' }}>
            {error}
          </p>
        )}

        <p className="unauthorized-hint">
          Если проблема не исчезает, попробуйте закрыть и снова открыть приложение.
        </p>
      </div>
    </div>
  );
};
